/**
 * @file AddPersonForm.tsx
 * @brief "Add someone": a new person = Name AND Email (B48, DES 02-11-2026).
 *
 * @details
 * One rule everywhere a new person enters VERBLIZR (a participant before,
 * during or after the meeting, a to-do's assignee): both fields are required
 * and the email must look like one. While the email is typed it is looked up
 * (`/api/meetings/participant-lookup`, the same debounced lookup as setup):
 * when it belongs to a VERBLIZR account the form shows that account's name and
 * photo and the account's own name is used (the typed name is ignored, as the
 * server does); otherwise the typed name is used everywhere.
 *
 * `fixedName` turns it into "Add email" for a guest who has a name only: the
 * name is shown, not edited.
 *
 * Keys typed here must not reach a Radix menu's typeahead, so the caller
 * keeps it inside an element that stops them (SearchableMenuList `header`
 * already does).
 *
 * @references
 * - docs/product/meeting-edit-after-start-b48.md (people rule)
 * - participantSearch.ts (useGuestLookup, FULL_EMAIL_RE)
 *
 * @author DES
 * @date 02-11-2026
 */

import { useState } from "react";
import { CircleCheck, Info, LoaderCircle, UserPlus } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FieldStyleContext } from "@/components/ui/fieldStyle";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import { toNameCase } from "./guessNameFromEmail";
import { newPersonError } from "./newPerson";
import { useGuestLookup } from "./participantSearch";

export type NewPerson = { name: string; email: string };

const NOT_BARE = { bare: false };

export function AddPersonForm({
  initialName = "",
  fixedName,
  submitLabel = "Add",
  busy = false,
  takenEmails,
  onAdd,
  onCancel,
}: {
  initialName?: string;
  /** "Add email" mode: this guest's name, shown and not editable. */
  fixedName?: string;
  submitLabel?: string;
  busy?: boolean;
  /** Emails already in the list: refused with "Already added". */
  takenEmails?: string[];
  onAdd: (p: NewPerson) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(fixedName ?? toNameCase(initialName.trim()));
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const lookup = useGuestLookup(email);
  const account =
    lookup.status === "found" || lookup.status === "inlist" ? (lookup.name ?? null) : null;
  const taken =
    !!email.trim() &&
    (takenEmails ?? []).some((e) => e.toLowerCase() === email.trim().toLowerCase());
  const error = taken ? "Already added." : newPersonError({ name, email }, fixedName ? null : account);
  const checking = lookup.status === "checking";

  const submit = () => {
    setTouched(true);
    if (error || busy || checking) return;
    onAdd({ name: toNameCase((fixedName ?? (account || name)).trim()), email: email.trim() });
  };

  return (
    // Its own pill fields with their own placeholders, also when it opens
    // inside a FormFieldGroup (the Assign-to picker), whose bare style would
    // otherwise put the group's label in both.
    <FieldStyleContext.Provider value={NOT_BARE}>
    <div
      className="space-y-2.5"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          submit();
        }
      }}
    >
      {fixedName ? null : (
        <Input
          capitalizeFirst
          value={account ?? name}
          disabled={!!account}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          aria-label="Name"
          autoFocus={!name}
          maxLength={120}
        />
      )}
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => email && setTouched(true)}
        placeholder="Email"
        aria-label="Email"
        autoFocus={!!name || !!fixedName}
        autoComplete="off"
      />
      {checking ? (
        <p className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
          Checking VERBLIZR…
        </p>
      ) : lookup.status === "found" ? (
        <div className="flex items-center gap-3 rounded-[1.15rem] bg-muted px-3 py-2">
          <Avatar
            src={lookup.profileImageUrl}
            alt={lookup.name}
            initials={getInitials(lookup.name || email)}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{lookup.name}</p>
            <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
              <CircleCheck className="h-3.5 w-3.5 shrink-0 text-brand" />
              VERBLIZR account · their own name is used
            </p>
          </div>
        </div>
      ) : null}
      {touched && error ? (
        <p className="flex items-center gap-1.5 px-1 text-xs text-destructive">
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : (
        <p className="px-1 text-[11px] leading-4 text-muted-foreground">
          {fixedName
            ? "Their email is saved on this meeting. It shares nothing and sends nothing."
            : "Name and email are both needed for someone new."}
        </p>
      )}
      <div className="flex gap-2">
        {onCancel ? (
          <Button type="button" variant="ghost" className="h-10 flex-1 rounded-full" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button
          type="button"
          // Never disabled while focused inside a menu: a disabled button
          // drops focus and Radix closes the menu. The error line says why.
          aria-disabled={busy || checking || (touched && !!error)}
          className={cn("h-10 flex-1 rounded-full", (busy || checking || (touched && !!error)) && "opacity-50")}
          onClick={submit}
        >
          {busy ? (
            <LoaderCircle className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <UserPlus className="mr-1.5 h-4 w-4" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
    </FieldStyleContext.Provider>
  );
}
